import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    Chip,
    // Chip,
    ClickAwayListener,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Popper,
    // Stack,
    Typography
} from '@mui/material';

// third-party
import { FormattedMessage } from 'react-intl';
// import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';

// assets
import {
    IconAsteriskSimple,
    IconLogout,
    IconPoint,
    IconUser,
} from '@tabler/icons-react';
import useConfig from 'hooks/useConfig';
import { logoutUser } from 'store/app/authentication/login/actions';
import { connect } from 'react-redux';

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = (props) => {

    const { userProfile } = props;
    const theme = useTheme();

    const { borderRadius } = useConfig();
    const navigate = useNavigate();

    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [open, setOpen] = useState(false);

    const anchorRef = useRef(null);
    const prevOpen = useRef(open);

    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const handleListItemClick = (event, index, route = '') => {
        setSelectedIndex(index);
        handleClose(event);

        if (route && route !== '') {
            navigate(route);
        }
    };

    const nameAvatar = (name) => {
        const username = name ? name.split(" ") : [];
        const avatarName = username.filter((i) => i !== "")
        if (name && name.length > 0) {
            if (avatarName && avatarName.length > 2) {
                return {
                    children: `${avatarName[0][0]}${avatarName[1][0]}`,
                };
            }

            else if (avatarName && avatarName.length === 2) {
                return {
                    children: `${avatarName[0][0]}${avatarName[1][0]}`,
                };
            }
            else if (avatarName && avatarName.length === 1) {
                return {
                    children: `${avatarName[0][0]}`,
                };
            }
        }
        else {
            return {
                children: <IconAsteriskSimple size={10} />
            }
        }
    }

    const handleLogout = async () => {
        props.logoutUser();
        navigate("/")
    };

    return !props.isAuth ? <Navigate to='/' /> : (
        <>
            <Avatar
                {...nameAvatar(userProfile?.displayName)}
                sx={{
                    ...theme.typography.mediumAvatar,
                    margin: '8px 0 8px 8px !important',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: "16px",
                    color: theme.palette.primary.dark,
                    backgroundColor: theme.palette.primary.light,
                }}
                ref={anchorRef}
                aria-controls={open ? 'menu-list-grow' : undefined}
                aria-haspopup="true"
                color="inherit"
                alt="user-account"
                onClick={handleToggle}
            />
            <Popper
                placement="bottom"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 14]
                        }
                    }
                ]}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions in={open} {...TransitionProps}>
                            <Paper sx={{ m: 2 }}>
                                {open && (
                                    <MainCard
                                        border={false}
                                        elevation={16}
                                        content={false}
                                        boxShadow
                                        shadow={theme.shadows[16]}
                                    >
                                        {/* <PerfectScrollbar
                                            style={{
                                                height: '100%',
                                                maxHeight: 'calc(100vh - 250px)',
                                                overflowX: 'hidden'
                                            }}
                                        > */}
                                        <Box>
                                            <List
                                                component="nav"
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: 350,
                                                    minWidth: 230,
                                                    backgroundColor: theme.palette.background.paper,
                                                    borderRadius: '10px',
                                                    [theme.breakpoints.down('md')]: {
                                                        minWidth: '100%'
                                                    },
                                                    '& .MuiListItemButton-root': {
                                                        mt: 0.5,
                                                        mb: 0.5,
                                                        ml: 1,
                                                        mr: 1,
                                                    },
                                                }}
                                            >
                                                <ListItem sx={{ paddingBottom: '0px' }}>
                                                    <ListItemText>
                                                        <Typography variant='h4' color='primary'>
                                                            {userProfile?.displayName}
                                                        </Typography>
                                                        <Chip
                                                            sx={{ bgcolor: theme.palette.primary.light }}
                                                            size='small'
                                                            icon={<IconPoint size={20} stroke={3} />}
                                                            label={
                                                                <Typography
                                                                    variant='h6'
                                                                    color='primary'
                                                                    sx={{
                                                                        fontWeight: 'bold',
                                                                        paddingTop: '1px',
                                                                    }}
                                                                >
                                                                    {userProfile?.userRoleName}
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItemText>
                                                </ListItem>
                                                <ListItemButton
                                                    sx={{ borderRadius: `${borderRadius}px` }}
                                                    selected={selectedIndex === 1}
                                                    onClick={(event) =>
                                                        handleListItemClick(event, 1, '/user/profile')
                                                    }
                                                >
                                                    <ListItemIcon>
                                                        <IconUser stroke={1.5} size="20px" />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Grid container spacing={1} justifyContent="space-between">
                                                                <Grid item>
                                                                    <Typography variant="body2" color='inherit'>
                                                                        <FormattedMessage id="profile" />
                                                                    </Typography>
                                                                </Grid>
                                                            </Grid>
                                                        }
                                                    />
                                                </ListItemButton>
                                                <Divider sx={{ mx: 2 }} />
                                                <ListItemButton
                                                    sx={{ borderRadius: `${borderRadius}px`, color: '#C41E3A' }}
                                                    selected={selectedIndex === 2}
                                                    onClick={handleLogout}
                                                >
                                                    <ListItemIcon >
                                                        <IconLogout stroke={1.5} size="20px" color='#C41E3A' />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="body2" color='#C41E3A'>
                                                                <FormattedMessage id="logout" />
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItemButton>
                                                <Divider sx={{ mx: 2 }} />
                                            </List>
                                        </Box>
                                        {/* </PerfectScrollbar> */}
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper >
        </>
    );
};

const mapStateToProps = (state) => ({
    isAuth: state.login.isAuth,
    userProfile: state.login.userProfile,
})

const mapDispatchToProps = (dispatch) => ({
    logoutUser: () => dispatch(logoutUser()),
})

export default connect(mapStateToProps, mapDispatchToProps)(ProfileSection);
